// ============================================
// Landing page: "Your first peptide, guided step by step"
// Pain 1 (first-dose panic) + Pain 5 (beginner overwhelm).
// Copy: docs/g2m-2026-07/02_landing_page_copy.md Page 3, with the
// CTA doctrine + honesty fixes from the G2M plan Section 5.
// ============================================

import React from 'react';
import { PainPage } from './PainPage';
import type { PainPageConfig } from './PainPage';

const config: PainPageConfig = {
  slug: 'your-first-peptide',
  path: '/your-first-peptide',
  metaTitle: 'Your First Peptide, Guided Step by Step | PRTCL',
  metaDescription:
    'You got the vial. Now what? PRTCL walks you from "I have no idea what I\'m doing" to "I just did it" - one calm step at a time. Free guide here, and the iOS app is on the App Store.',
  eyebrow: 'YOUR FIRST PEPTIDE',
  h1: (
    <>
      Your first peptide, guided <span className="lp-accent">step by step.</span>
    </>
  ),
  h1Text: 'Your first peptide, guided step by step.',
  sub: 'You got the vial. Now what? PRTCL walks you from "I have no idea what I\'m doing" to "I just did it" - one calm step at a time, the first time.',
  heroCtaLabel: 'Get the free first-timer guide',
  secondary: { label: 'Or start with Peptides 101, free', to: '/learn' },
  sections: [
    {
      heading: 'Name the fear',
      lead: 'The vial is sitting there, and so is the panic.',
      body: (
        <>
          <p>
            You are not alone in this. One first-timer, holding their first pen, wrote that they were
            "feeling overwhelmed and hesitant about actually using it." Another spent "nearly an hour
            in a state of panic while trying to get everything ready." The fear is normal. It just
            means you care about getting it right.
          </p>
        </>
      ),
      quote: {
        text: '"If someone could walk me through it in very simple, step-by-step terms (as if I\'ve never taken a chemistry class), that would be extremely helpful."',
        attribution: 'Community member, r/Peptidesource',
      },
    },
    {
      heading: 'The wish',
      lead: 'People keep asking for the exact thing PRTCL is.',
      body: (
        <>
          <p>
            Over and over, beginners plead for the same thing: walk me through it, one step at a time,
            in plain words. That is PRTCL. Not a medical dashboard. Not a wall of research papers. A
            friend who has done this a hundred times, showing you the current step and nothing else.
          </p>
        </>
      ),
    },
    {
      heading: 'The promise',
      lead: 'One step on the screen at a time. Never more than you need right now.',
      body: (
        <>
          <p>
            PRTCL shows you the current step and nothing else. Mix the vial. Then draw the dose. Then
            the injection. Each step has a picture and plain words. When you finish, PRTCL says what
            everyone wishes someone had told them: you did it, and you did it right.
          </p>
        </>
      ),
    },
  ],
  steps: [
    { title: 'Tell PRTCL your peptide.', body: 'It sets up the right steps for you, with the common ones preset.' },
    { title: 'Follow one step at a time.', body: 'Mixing, drawing, injecting. Each with a picture and plain words.' },
    { title: 'Cross the finish line.', body: 'PRTCL confirms each step so you always know you are on track.' },
  ],
  reassurance: {
    heading: 'The needle is thinner than a hair.',
    body: 'A small bubble will not hurt you. Feeling scared before the first one is so common that people post about it daily. PRTCL names each fear as it comes up, then shows you it is simpler than it looks.',
  },
  faqs: [
    {
      q: 'I have never given myself a shot. Is this for me?',
      a: 'Especially for you. PRTCL is built for the first-timer, not the expert. Everything starts at plain, beginner level.',
    },
    {
      q: 'What if I mess up a step?',
      a: 'You can go back anytime. PRTCL never rushes you and never assumes you already know.',
    },
    {
      q: 'Is this medical advice?',
      a: 'No. PRTCL is educational and shows commonly reported steps. It is here to help you feel confident, not to replace your doctor.',
      defaultOpen: true,
    },
    {
      q: 'Where do I get the app?',
      a: 'PRTCL is on the App Store now, and the download is free. Tap the App Store button on this page to get it on your iPhone. Want the first-timer guide too? Drop your email below and it is yours. The free Peptides 101 guide and reconstitution calculator stay on this site either way.',
      defaultOpen: true,
    },
  ],
  magnet: { name: 'What to Know Before Your First Peptide guide', href: '/downloads/before-your-first-peptide.pdf' },
  magnetHeadline: 'Read ahead. Walk in feeling ready.',
  magnetLede:
    'Get "What to Know Before Your First Peptide" free: the calm, plain-language primer on what a peptide is, what the first time actually looks like, and the fears everyone has (named and dissolved). You will also get short notes and new guides as we publish them. PRTCL itself is on the App Store now, free to download.',
};

const FirstPeptidePage: React.FC = () => <PainPage config={config} />;

export default FirstPeptidePage;
